import type { Tournament, TournamentParticipant, TournamentPrize, TournamentSettings } from '../../types/features';
import { playerService } from '../player/playerService';

class TournamentService {
    private static instance: TournamentService;
    private tournaments: Tournament[] = [
        {
            id: 'weekend-spins',
            name: 'Weekend Spins Challenge',
            description: 'Join our weekend tournament and compete for massive prizes! Show your spinning skills and climb the leaderboard.',
            imageUrl: 'https://images.unsplash.com/photo-1518544801976-3e159e50e5bb?w=800&h=400&fit=crop',
            startDate: '2024-03-23',
            endDate: '2024-03-24',
            status: 'active',
            type: 'public',
            mode: 'friends_party',
            entryFee: 10,
            prizePool: 1000,
            isPrizePoolProgressive: true,
            minParticipants: 2,
            maxParticipants: 100,
            scoringStrategy: 'max_multiplier',
            settings: {
                spinLimit: 100,
                minBet: 1,
                includeBonusSpins: false,
                includePurchasedSpins: true,
                joinDeadlineHours: 2
            },
            prizes: [
                { position: 1, amount: 50, isPercentage: true },
                { position: 2, amount: 30, isPercentage: true },
                { position: 3, amount: 20, isPercentage: true }
            ],
            participants: [
                {
                    id: 'player1',
                    nickname: 'SpinMaster',
                    score: 15,
                    position: 1,
                    joinedAt: '2024-03-22T10:00:00Z'
                },
                {
                    id: 'player2',
                    nickname: 'LuckySpinner',
                    score: 12,
                    position: 2,
                    joinedAt: '2024-03-22T11:30:00Z'
                }
            ],
            allowLateJoin: true,
            createdBy: 'admin',
            createdAt: '2024-03-20T00:00:00Z',
            updatedAt: '2024-03-20T00:00:00Z'
        },
        {
            id: 'high-roller-showdown',
            name: 'High Roller Showdown',
            description: 'Exclusive tournament for high rollers! Higher stakes, bigger prizes, and intense competition.',
            imageUrl: 'https://images.unsplash.com/photo-1533228876829-65c94e7b5025?w=800&h=400&fit=crop',
            startDate: '2024-03-25',
            endDate: '2024-03-31',
            status: 'upcoming',
            type: 'private',
            mode: 'influencer_party',
            inviteCode: 'VIP2024',
            entryFee: 100,
            prizePool: 10000,
            isPrizePoolProgressive: false,
            minParticipants: 5,
            maxParticipants: 50,
            scoringStrategy: 'classic',
            settings: {
                minBet: 50,
                includeBonusSpins: false,
                includePurchasedSpins: true
            },
            prizes: [
                { position: 1, amount: 5000, isPercentage: false },
                { position: 2, amount: 3000, isPercentage: false },
                { position: 3, amount: 2000, isPercentage: false }
            ],
            participants: [],
            allowLateJoin: false,
            createdBy: 'admin',
            createdAt: '2024-03-21T00:00:00Z',
            updatedAt: '2024-03-21T00:00:00Z'
        },
        {
            id: 'multiplier-mania',
            name: 'Multiplier Mania',
            description: 'Chase the biggest multipliers in this exciting tournament! Every spin counts towards your total score.',
            imageUrl: 'https://images.unsplash.com/photo-1513151233558-d860c5398176?w=800&h=400&fit=crop',
            startDate: '2024-03-24',
            endDate: '2024-03-25',
            status: 'active',
            type: 'public',
            mode: 'friends_party',
            entryFee: 5,
            prizePool: 500,
            isPrizePoolProgressive: true,
            minParticipants: 10,
            scoringStrategy: 'total_multiplier',
            settings: {
                spinLimit: 50,
                minBet: 0.5,
                includeBonusSpins: true,
                includePurchasedSpins: true,
                maxMultipliersCount: 10
            },
            prizes: [
                { position: 1, positionEnd: 3, amount: 60, isPercentage: true },
                { position: 4, positionEnd: 10, amount: 40, isPercentage: true }
            ],
            participants: [],
            allowLateJoin: true,
            createdBy: 'admin',
            createdAt: '2024-03-22T00:00:00Z',
            updatedAt: '2024-03-22T00:00:00Z'
        }
    ];

    private constructor() {
        console.log('TournamentService initialized with tournaments:', this.tournaments);
    }

    static getInstance(): TournamentService {
        if (!TournamentService.instance) {
            TournamentService.instance = new TournamentService();
        }
        return TournamentService.instance;
    }

    getTournaments(): Tournament[] {
        return [...this.tournaments];
    }

    getTournamentById(id: string): Tournament | undefined {
        return this.tournaments.find(t => t.id === id);
    }

    getActiveTournaments(): Tournament[] {
        const active = this.tournaments.filter(t => t.status === 'active');
        console.log('Getting active tournaments:', active);
        return active;
    }

    getUpcomingTournaments(): Tournament[] {
        const upcoming = this.tournaments.filter(t => t.status === 'upcoming');
        console.log('Getting upcoming tournaments:', upcoming);
        return upcoming;
    }

    createTournament(tournament: Omit<Tournament, 'id' | 'status' | 'participants' | 'createdAt' | 'updatedAt'>): Tournament {
        const newTournament: Tournament = {
            ...tournament,
            id: `tournament-${Date.now()}`,
            status: 'draft',
            participants: [],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        };

        this.tournaments.push(newTournament);
        return newTournament;
    }

    updateTournament(id: string, updates: Partial<Tournament>): Tournament | undefined {
        const index = this.tournaments.findIndex(t => t.id === id);
        if (index === -1) return undefined;

        const tournament = this.tournaments[index];
        if (tournament.status === 'active' || tournament.status === 'completed') {
            throw new Error('Cannot update active or completed tournament');
        }

        const updatedTournament = {
            ...tournament,
            ...updates,
            updatedAt: new Date().toISOString()
        };

        this.tournaments[index] = updatedTournament;
        return updatedTournament;
    }

    joinTournament(tournamentId: string, playerId: string): boolean {
        const tournament = this.tournaments.find(t => t.id === tournamentId);
        if (!tournament) return false;

        if (tournament.status !== 'active' && tournament.status !== 'upcoming') {
            return false;
        }

        if (tournament.participants.some(p => p.id === playerId)) {
            return false;
        }

        const player = playerService.getProfile();
        const participant: TournamentParticipant = {
            id: playerId,
            nickname: player.nickname,
            score: 0,
            position: tournament.participants.length + 1,
            joinedAt: new Date().toISOString()
        };

        tournament.participants.push(participant);
        return true;
    }

    updateScore(tournamentId: string, playerId: string, score: number): void {
        const tournament = this.tournaments.find(t => t.id === tournamentId);
        if (!tournament || tournament.status !== 'active') return;

        const participant = tournament.participants.find(p => p.id === playerId);
        if (!participant) return;

        participant.score = score;

        // Update positions
        tournament.participants.sort((a, b) => b.score - a.score);
        tournament.participants.forEach((p, index) => {
            p.position = index + 1;
        });
    }

    calculatePrize(tournament: Tournament, position: number): number {
        const prize = tournament.prizes.find(
            p => p.position === position || (p.positionEnd && position >= p.position && position <= p.positionEnd)
        );

        if (!prize) return 0;

        if (prize.isPercentage) {
            return (tournament.prizePool * prize.amount) / 100;
        }

        return prize.amount;
    }
}

export const tournamentService = TournamentService.getInstance(); 